import React, { useEffect, useState } from 'react';
import { Notification } from '@shootsta/common-react';

const STOCK_UNLIMITED_BANNER_DURATION_MS = 48 * 60 * 60 * 1000;

type Props = {
  enabled: string | boolean;
  organisationId?: string;
  dismissedNotificationForOrg: string | null;
  onDismiss: (orgId?: string) => void;
  getOrganisationStockAccess: Function;
};

export default function StockUnlimitedBanner({
  enabled,
  organisationId,
  dismissedNotificationForOrg,
  onDismiss,
  getOrganisationStockAccess
}: Props) {
  const [shouldShow, setShouldShow] = useState<boolean | null>(null);

  const notificationText = enabled;
  const isStockUnlimitedBanner =
    typeof notificationText === 'string' &&
    notificationText.toLowerCase().includes('stock unlimited');

  useEffect(() => {
    if (!isStockUnlimitedBanner || !organisationId) {
      setShouldShow(true);
      return;
    }

    const checkBannerState = async () => {
      const storageKey = `stockUnlimitedBannerFirstSeen:${organisationId}`;
      const dismissedKey = `stockUnlimitedBannerDismissed:${organisationId}`;
      const stockAccessKey = `stockUnlimitedWasEnabled:${organisationId}`;

      let currentStockAccess = true;

      // Fetch current shutterstockAccess from backend
      try {
        const { data } = await getOrganisationStockAccess({ organisationId });
        currentStockAccess = data?.getOrganisation?.shutterstockAccess ?? true;
      } catch (error) {
        console.error('Failed to fetch stock access:', error);
      }

      const wasEnabled = localStorage.getItem(stockAccessKey);
      const isDismissed = localStorage.getItem(dismissedKey);
      const firstSeen = localStorage.getItem(storageKey);
      const now = Date.now();

      // If Stock Unlimited was disabled and is now re-enabled, reset dismissal
      if (wasEnabled === 'false' && currentStockAccess) {
        localStorage.removeItem(dismissedKey);
        localStorage.removeItem(storageKey);
        localStorage.setItem(stockAccessKey, 'true');
        localStorage.setItem(storageKey, now.toString());
        setShouldShow(true);
        return;
      }

      // Store current stock access state
      localStorage.setItem(stockAccessKey, String(currentStockAccess));

      // If dismissed, don't show
      if (isDismissed) {
        setShouldShow(false);
        return;
      }

      // Handle 48-hour window
      if (!firstSeen) {
        localStorage.setItem(storageKey, now.toString());
        setShouldShow(true);
      } else if (now - Number(firstSeen) > STOCK_UNLIMITED_BANNER_DURATION_MS) {
        localStorage.removeItem(dismissedKey);
        localStorage.removeItem(storageKey);
        localStorage.removeItem(stockAccessKey);
        setShouldShow(false);
      } else {
        setShouldShow(true);
      }
    };

    checkBannerState();
  }, [isStockUnlimitedBanner, organisationId, getOrganisationStockAccess]);

  if (!enabled || dismissedNotificationForOrg === organisationId) {
    return null;
  }

  // Loading state
  if (shouldShow === null) {
    return null;
  }

  if (isStockUnlimitedBanner && !shouldShow) {
    return null;
  }

  return (
    <div style={{ width: '100%' }}>
      <Notification
        fullWidthStyle
        text={notificationText}
        onClickCloseBtn={() => {
          if (isStockUnlimitedBanner && organisationId) {
            localStorage.setItem(
              `stockUnlimitedBannerDismissed:${organisationId}`,
              'true'
            );
          }
          onDismiss(organisationId);
        }}
      />
    </div>
  );
}
